export type GameCategory = 'individual' | 'team' | 'rotating' | 'scramble' | 'events' | 'wager';

export interface CatalogEntry {
  id: string;
  label: string;
  description: string;
  category: GameCategory;
  pluginId: string;
  minPlayers: number;
  maxPlayers: number;
  requiresTeams: boolean;
  /** Event-only formats use tournament templates instead of group-round plugins */
  eventTemplate?: boolean;
}

export const GAME_CATALOG: CatalogEntry[] = [
  // Individual
  { id: 'stroke', label: 'Stroke Play', description: 'Lowest gross score wins', category: 'individual', pluginId: 'stroke', minPlayers: 1, maxPlayers: 4, requiresTeams: false },
  { id: 'net-stroke', label: 'Net Stroke', description: 'Lowest net score wins', category: 'individual', pluginId: 'net-stroke', minPlayers: 1, maxPlayers: 4, requiresTeams: false },
  { id: 'stableford', label: 'Stableford', description: 'Most points vs par wins', category: 'individual', pluginId: 'stableford', minPlayers: 2, maxPlayers: 4, requiresTeams: false },
  { id: 'match-play', label: 'Match Play', description: 'Head-to-head holes won', category: 'individual', pluginId: 'match-play', minPlayers: 2, maxPlayers: 4, requiresTeams: false },
  { id: 'quota', label: 'Quota', description: 'Beat your handicap target', category: 'individual', pluginId: 'quota', minPlayers: 2, maxPlayers: 4, requiresTeams: false },
  { id: 'dots', label: 'Dots / Junk', description: 'Birdies, sandies, greenies and more', category: 'individual', pluginId: 'dots', minPlayers: 4, maxPlayers: 4, requiresTeams: false },

  // Wager (core side games)
  { id: 'skins', label: 'Skins', description: 'Win the hole outright, take the skin', category: 'wager', pluginId: 'skins', minPlayers: 2, maxPlayers: 4, requiresTeams: false },
  { id: 'nassau', label: 'Nassau', description: 'Front 9, back 9, and overall', category: 'wager', pluginId: 'nassau', minPlayers: 2, maxPlayers: 4, requiresTeams: false },
  { id: 'team-nassau', label: 'Team Nassau', description: '2v2 — front, back, and overall', category: 'wager', pluginId: 'team-nassau', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'putts-bet', label: 'Putts Bet', description: 'Fewest putts wins or pay per putt over the leader', category: 'wager', pluginId: 'putts-bet', minPlayers: 2, maxPlayers: 4, requiresTeams: false },

  // Team
  { id: 'best-ball', label: 'Best Ball', description: 'Team best net per hole', category: 'team', pluginId: 'best-ball', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'better-ball', label: 'Better Ball', description: 'Same as Best Ball', category: 'team', pluginId: 'best-ball', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'four-ball', label: 'Four Ball', description: '2v2 partner match play', category: 'team', pluginId: 'four-ball', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'aggregate', label: 'Aggregate', description: 'Sum both net scores per hole', category: 'team', pluginId: 'aggregate', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'high-low', label: 'High-Low', description: 'Best + worst net combined', category: 'team', pluginId: 'high-low', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'team-stableford', label: 'Team Stableford', description: 'Team best net → Stableford pts', category: 'team', pluginId: 'team-stableford', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'team-match-play', label: 'Team Match Play', description: 'Team vs team holes won', category: 'team', pluginId: 'team-match-play', minPlayers: 4, maxPlayers: 4, requiresTeams: true },

  // Rotating
  { id: 'wolf', label: 'Wolf', description: 'Blind Wolf, pick/pass partner, or go lone — rotating tee order', category: 'rotating', pluginId: 'wolf', minPlayers: 4, maxPlayers: 4, requiresTeams: false },
  { id: 'lone-wolf', label: 'Lone Wolf', description: 'Wolf-only variant with higher stakes', category: 'rotating', pluginId: 'wolf', minPlayers: 4, maxPlayers: 4, requiresTeams: false },
  { id: 'vegas', label: 'Vegas', description: '2v2 — team scores form a number', category: 'rotating', pluginId: 'vegas', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'sixes', label: 'Sixes', description: 'Rotating partners every 6 holes', category: 'rotating', pluginId: 'sixes', minPlayers: 4, maxPlayers: 4, requiresTeams: false },

  // Scramble
  { id: 'scramble', label: 'Scramble', description: 'Team plays best shot each time', category: 'scramble', pluginId: 'scramble', minPlayers: 2, maxPlayers: 4, requiresTeams: true },
  { id: 'shamble', label: 'Shamble', description: 'Best drive, own ball in', category: 'scramble', pluginId: 'shamble', minPlayers: 2, maxPlayers: 4, requiresTeams: true },

  // Events
  { id: 'ryder-cup', label: 'Ryder Cup', description: 'Team match play series', category: 'events', pluginId: 'ryder-cup', minPlayers: 4, maxPlayers: 4, requiresTeams: true },
  { id: 'tournament', label: 'Tournament', description: 'Multi-round club event', category: 'events', pluginId: 'tournament', minPlayers: 2, maxPlayers: 4, requiresTeams: false, eventTemplate: true },
  { id: 'member-guest', label: 'Member-Guest', description: 'Member + guest pairs event', category: 'events', pluginId: 'member-guest', minPlayers: 4, maxPlayers: 4, requiresTeams: true, eventTemplate: true },
  { id: 'club-championship', label: 'Club Championship', description: 'Club stroke play championship', category: 'events', pluginId: 'club-championship', minPlayers: 2, maxPlayers: 4, requiresTeams: false, eventTemplate: true },
  { id: 'league-play', label: 'League Play', description: 'Season-long league standings', category: 'events', pluginId: 'league-play', minPlayers: 2, maxPlayers: 4, requiresTeams: false, eventTemplate: true },
];

export const CATEGORY_LABELS: Record<GameCategory, string> = {
  individual: 'Individual',
  team: 'Team',
  rotating: 'Rotating',
  scramble: 'Scramble',
  events: 'Events',
  wager: 'Wager',
};

export const CATEGORY_ORDER: GameCategory[] = ['individual', 'wager', 'team', 'rotating', 'scramble', 'events'];

export function findCatalogEntry(id: string): CatalogEntry | undefined {
  return GAME_CATALOG.find((g) => g.id === id);
}

export function catalogEntriesForCategory(cat: GameCategory): CatalogEntry[] {
  return GAME_CATALOG.filter((g) => g.category === cat);
}

export function groupRoundGameIds(): string[] {
  return [...new Set(GAME_CATALOG.filter((g) => !g.eventTemplate).map((g) => g.pluginId))];
}

export function isGameAvailable(entry: CatalogEntry, playerCount: number): boolean {
  if (playerCount < entry.minPlayers || playerCount > entry.maxPlayers) return false;
  return true;
}

export function catalogLabel(id: string): string {
  return findCatalogEntry(id)?.label ?? id;
}

export function pluginIdFor(formatId: string): string {
  return findCatalogEntry(formatId)?.pluginId ?? formatId;
}

/** Unique picker entries (hide better-ball alias duplicate in UI) */
export function pickerCatalogEntries(): CatalogEntry[] {
  const seen = new Set<string>();
  return GAME_CATALOG.filter((g) => {
    if (g.eventTemplate) return true;
    if (g.id === 'better-ball') return false;
    const key = g.pluginId + g.category;
    if (seen.has(key) && g.id === 'lone-wolf') return true;
    if (seen.has(g.pluginId) && g.pluginId !== 'wolf') return false;
    seen.add(g.pluginId);
    return true;
  });
}

export const GAME_LABELS: Record<string, string> = Object.fromEntries(
  GAME_CATALOG.map((g) => [g.id, g.label]),
);
